<template>
    <el-form class="bank-account-form" ref="form" :model="form" :rules="rules" label-position="right" label-width="210px">
        <el-form-item v-if="!lite" prop="cny" label="類型">
            <el-radio-group v-model="form.typ" :disabled="!isCreateMode" @change="onChangeTyp">
                <el-radio-button :label="0"> 存款戶 </el-radio-button>
                <el-radio-button :label="1"> 暫存戶 </el-radio-button>
            </el-radio-group>
        </el-form-item>
        <el-form-item prop="tpc" label="帳戶簡稱">
            <el-input v-model="form.tpc" placeholder="顯示名稱" :maxlength="50" :show-word-limit="true"></el-input>
        </el-form-item>
        <el-form-item v-if="!lite" label="警示餘額">
            <money-input v-model.number="form.wrN_AMT" v-if="form.wrN_FLG == 1" />
            <el-checkbox v-model="form.wrN_FLG" :true-label="1" :false-label="0"></el-checkbox>
            <span class="wrn-info">( 餘額低於警示餘額時，將標註於存款列表 )</span>
        </el-form-item>
        <el-form-item v-if="!lite" label="借款戶">
            <el-checkbox v-model="form.isBorrower"></el-checkbox>
            <span class="wrn-info">( 於績效計分表中另列為借款 )</span>
        </el-form-item>
        <el-form-item prop="dat" label="在Sokuji系統上的開帳日期">
            <el-date-picker v-model="form.dat" value-format="timestamp" type="date" placeholder="選擇日期" default-time="00:00:00" :picker-options="expireTimeOption"> </el-date-picker>
        </el-form-item>
        <el-form-item v-if="isCreateMode" prop="amt" label="開帳日前一天的餘額">
            <money-input v-model="form.amt" :precision="form.cny == 'TWD' ? 0 : 4" :disabled="!isCreateMode" allow-negative style="width: 300px" />
            <ah-tip>{{ amtExampleText }}</ah-tip>
        </el-form-item>
        <el-form-item v-else prop="amt" label="開帳日前一天的餘額">
            <money-input v-model="form.original_AMT" :precision="form.cny == 'TWD' ? 0 : 4" style="width: 300px" />
            <ah-tip>{{ amtExampleText }}</ah-tip>
        </el-form-item>
        <el-form-item prop="cny" label="幣別">
            <ah-el-select-cny v-model="form.cny" :disabled="!isCreateMode" @change="onCnyChange"></ah-el-select-cny>
            <ah-tip>不同幣別請分開建立帳戶，例：中國信託－台幣、中國信託－美金</ah-tip>
        </el-form-item>
        <el-form-item v-if="((isPayed('G02') && isAuth('F03')) || isAuth('admin')) && !lite" prop="accno" label="會計科目">
            <ah-select-accounting-setting
                v-model="form.accno"
                :allowedItems="form.typ === 0 ? ['1105', '1110', '1111', '1112', '1113', '1114', '1115'] : []"
                :allowedGroups="['assetsSettings']"
                clearable
            />
        </el-form-item>
        <el-form-item prop="enableDocumentInUse" label="使用狀態">
            <el-radio-group v-model="form.enableDocumentInUse">
                <el-radio :label="true">啟用</el-radio>
                <el-radio :label="false">停用</el-radio>
            </el-radio-group>
        </el-form-item>
        <el-form-item v-if="form.enableDocumentInUse" prop="documentInUse" label="此帳戶可使用於">
            <el-checkbox-group v-model="form.documentInUse" style="display: flex; flex-direction: column">
                <el-checkbox v-for="item in bankAccountDocumentInUseOptions" :key="item.key" :label="item.key" style="width: fit-content">{{ item.label }}</el-checkbox>
            </el-checkbox-group>
        </el-form-item>
        <div class="alert">
            <div class="icon el-icon-warning-outline"></div>
            <div class="text">以下資料送出後皆不可修改</div>
        </div>
        <el-form-item v-if="form.typ === 0" prop="bnK_BOO" label="銀行">
            <ah-el-select-bank v-model="form.bnK_BOO" :branch="form.bnK_DOO" @branchInput="form.bnK_DOO = $event" :disabled="!isCreateMode"></ah-el-select-bank>
        </el-form-item>
        <el-form-item v-if="form.typ === 0" prop="bnK_ACC" label="帳號">
            <ah-el-input-bank-acc v-model="form.bnK_ACC" :bankBoo="form.bnK_BOO" :disabled="!isCreateMode"></ah-el-input-bank-acc>
        </el-form-item>
        <el-form-item v-if="form.typ === 0" prop="bnK_NAM" label="存摺戶名">
            <el-input v-model="form.bnK_NAM" :disabled="!isCreateMode" :maxlength="20" :show-word-limit="true"></el-input>
        </el-form-item>
        <el-form-item v-if="!isCreateMode" prop="amt" label="當前餘額">
            <money-input v-model="form.amt" :precision="form.cny == 'TWD' ? 0 : 2" :disabled="!isCreateMode" style="width: 300px" />
        </el-form-item>
    </el-form>
</template>
<script>
import ahElSelectBank from '@/components/ui/ahElSelectBank.vue';
import ahElInputBankAcc from '@/components/ui/ahElInputBankAcc.vue';
import ahElSelectCny from '@/components/ui/ahElSelectCny.vue';
import ahSelectAccountingSetting from '@/components/ui/ahSelectAccountingSetting.vue';
import moment from 'moment';
import { mapGetters } from 'vuex';
import { bankAccountDocumentInUseOptions } from '@/assets/commonData/options';

const AHOO_BANK_BOO = '000';
const AHOO_BANK_DOO = '000000';

export default {
    name: 'BankAccountEditPage',
    props: {
        lite: {
            type: Boolean,
            default: false,
        },
        staffPermissions: {
            type: Array,
            default: () => [],
        },
        permissionTyp: {
            type: Number,
            default: 1,
        },
    },
    components: {
        ahElSelectBank,
        ahElInputBankAcc,
        ahElSelectCny,
        ahSelectAccountingSetting,
    },
    data() {
        return {
            bankAccountDocumentInUseOptions,
            form: {
                sno: null,
                tpc: '',
                bnK_BOO: '',
                bnK_DOO: '',
                bnK_ACC: '',
                bnK_NAM: '',
                dat: Number(moment().startOf('day').format('x')),
                enableDocumentInUse: true,
                documentInUse: [1, 2, 3, 4, 5, 6, 7, 8],
                typ: 0, // 0: 存款戶, 1: 暫存戶
                cny: 'TWD',
                amt: 0,
                wrN_FLG: 0,
                wrN_AMT: 0,
                isBorrower: false,
                original_AMT: null,
                accno: null,
            },
            rules: {
                tpc: [
                    {
                        required: true,
                        message: '請輸入帳戶簡稱',
                        trigger: 'blur',
                    },
                ],
                bnK_NAM: [
                    {
                        required: true,
                        message: '請輸入存摺戶名',
                        trigger: 'blur',
                    },
                ],
                bnK_BOO: [
                    {
                        required: true,
                        message: '請選擇銀行',
                        trigger: 'change',
                    },
                ],
                bnK_DOO: [
                    {
                        required: true,
                        message: '請選擇分行',
                        trigger: 'change',
                    },
                ],
                bnK_ACC: [{ required: true, message: '請輸入帳號', trigger: 'blur' }],
                dat: [
                    {
                        required: true,
                        message: '請選擇在 Sokuji 系統上的開帳日期',
                        trigger: 'change',
                    },
                ],
                amt: [
                    {
                        required: true,
                        message: '請輸入初始金額',
                        trigger: 'blur',
                    },
                ],
                accno: [
                    {
                        required: true,
                        message: '請選擇會計科目',
                        trigger: 'blur',
                    },
                ],
            },
            expireTimeOption: {
                disabledDate(date) {
                    return date.getTime() >= Date.now();
                },
            },
            oldOriginal_AMT: null,
        };
    },
    computed: {
        ...mapGetters({
            companyInfo: 'menu/companyInfo',
            accountingSettings: 'accounting/accountingSettings',
        }),
        isCreateMode() {
            return this.form.sno === null || this.form.sno === undefined;
        },
        amtExampleText() {
            return '例如：帳戶啟用日期2022/1/1，請登錄2021/12/31之餘額。';
        },
    },
    async created() {
        if (this.isAuth('F03')) await this.$store.dispatch('accounting/updateAccountingSettings');
        if (this.$route.params.sno) this.fetchData();
        else {
            this.form.bnK_NAM = this.companyInfo.cH_CMP;
            this.form.accno = '1110';
        }
    },
    methods: {
        onCnyChange(val) {
            let found;
            switch (val) {
            case 'TWD':
                this.form.accno = '1110';
                break;
            case 'USD':
                found = this.accountingSettings.assetsSettings.find((o) => o.accno.includes('1112-01'));
                this.form.accno = found && found.accnam.includes('美金') ? '1112-01' : '1112';
                break;
            case 'JPY':
                found = this.accountingSettings.assetsSettings.find((o) => o.accno.includes('1112-02'));
                this.form.accno = found && found.accnam.includes('日元') ? '1112-02' : '1112';
                break;
            case 'EUR':
                found = this.accountingSettings.assetsSettings.find((o) => o.accno.includes('1112-03'));
                this.form.accno = found && found.accnam.includes('歐元') ? '1112-03' : '1112';
                break;
            default:
                this.form.accno = '1112';
            }
        },
        async fetchData() {
            // const loading = this.$loading({
            //     lock: true,
            //     text: '載入中',
            //     spinner: 'el-icon-loading',
            // });
            try {
                console.log('hihihi');
                const resp = await this.$api({
                    url: `/BankAccountSettingPage/GetSettingDetail?SNO=${this.$route.params.sno}`,
                    method: 'GET',
                });

                console.log('bank account', resp.data);
                this.form = resp.data.setting;
                this.oldOriginal_AMT = this.form.original_AMT;
                this.$emit('fetch', {
                    sno: this.form.sno,
                    isUsed: this.form.isUsed,
                    staffPermissions: this.form.staffPermissions,
                    permissionTyp: this.form.permissionTyp,
                });
            } catch (e) {
                console.log(e);
            } finally {
                // loading.close();
            }
        },

        onSubmit(callback) {
            const data = { ...this.form, amt: undefined };
            console.log(data);

            this.$refs.form.validate(async (valid) => {
                if (valid) {
                    if (this.isCreateMode) {
                        data.amt = this.form.amt;
                    } else if (this.oldOriginal_AMT !== this.form.original_AMT) {
                        data.amt = this.form.original_AMT;
                    } else data.amt = this.oldOriginal_AMT;

                    await this.$api({
                        url: '/BankAccountSettingPage/UpsertSetting',
                        method: 'post',
                        data: { ...data, staffPermissions: this.staffPermissions, permissionTyp: this.permissionTyp },
                    });

                    if (callback) callback();

                    return true;
                }
                return false;
            });
        },

        onChangeTyp() {
            if (this.form.typ === 1) {
                this.form.bnK_BOO = AHOO_BANK_BOO;
                this.form.bnK_DOO = AHOO_BANK_DOO;
                this.form.bnK_ACC = `${+new Date()}`;
                this.form.bnK_NAM = `${this.companyInfo.cH_CMP}（暫存戶）`;
            } else {
                this.form.bnK_BOO = '';
                this.form.bnK_DOO = '';
                this.form.bnK_ACC = '';
                this.form.bnK_NAM = `${this.companyInfo.cH_CMP}`;
                this.$nextTick(() => {
                    this.$refs.form.clearValidate();
                });
            }
        },
    },
};
</script>
<style lang="scss">
.bank-account-form {
    padding: 0 30px;
    .wrn-info {
        font-size: 9pt;
        opacity: 0.8;
        margin-left: 10px;
    }
    .alert {
        color: #f74848;
        font-weight: bold;
        font-size: 10.5pt;
        display: flex;
        align-items: center;
        margin: 0 auto 20px;
        padding-bottom: 5px;
        border-bottom: 1px solid #f74848;
        .icon {
            font-size: 15pt;
            margin-right: 10px;
        }
    }
}
</style>
